/**
 * Jeden silnik produkcji i zwrotu dla klienta: Projekt 3D, oferta (VoltValue Projekt, `/sprawdz-oferte`)
 * i karta klienta liczą przez TĘ funkcję, więc te same dane dają ten sam zwrot.
 *
 * TU NIE MA NOWEJ FIZYKI ani nowych pieniędzy. Autokonsumpcja, magazyn i degradacja to `prognoza`
 * (`prognozaProdukcji.js`, krzywa z kalkulatora mocy), wartość energii, zwrot i porównanie z magazynem to
 * `oplacalnosc.js`, zużycie to `zuzycieRoczne` z kalkulatora mocy. Ten plik rozstrzyga tylko, SKĄD brać
 * wejście, i mówi to w `zrodlaDanych`:
 *
 *   produkcja  Projekt 3D (prawdziwe połacie i cień) → zapisana w profilu produkcja z 3D → tabela
 *              województwa × ustawienie × cień
 *   koszt      cena klienta (brutto z karty) → kwota wpisana → suma zestawu → podane widełki → typowe
 *              widełki z `podpowiedzKosztu`
 *   magazyn    zawsze pojemność użytkowa i sprawność z karty katalogowej, gdy je znamy
 *   falownik   wymiana w 12. roku za cenę z zestawu, a bez niej 12% kosztu
 *   ceny       taryfa klienta (G11, G12, G12w; „nie wiem” jak G11) i jego ceny z rachunku, inaczej domyślne
 *              ceny taryfy z `config/taryfy.js`; rozliczenie depozytu w `lib/rozliczenie.js`
 *
 * Wcześniej Projekt 3D i oferta wołały kalkulatory każde po swojemu (inny koszt, inna pojemność magazynu,
 * inna wymiana falownika) i ten sam klient dostawał dwa różne zwroty.
 */

import { prognoza } from './prognozaProdukcji'
import {
  przeplywy, porownajZMagazynem, podpowiedzKosztu, WZROST_CEN_DOMYSLNY,
} from './oplacalnosc'
import { rozliczenieKlienta, wartoscEnergii } from './rozliczenie'
import { jestPolska } from '../kraj'
import {
  normalizujEnergie, rocznyKwh, kluczUlgi, wojewodztwoZeWspolrzednych,
} from './klienci/energia'

/* ═══ Pomocnicze ═════════════════════════════════════════════════════════════════════════════ */

/** Liczba dodatnia z liczby albo tekstu („35 000”, „4,5”); wszystko inne to 0. */
const dodatnia = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/\s/g, '').replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * 12 nieujemnych liczb (choć jedna dodatnia, bo same zera to projekt bez modułów) albo `null`.
 * Tu bez wyjątku: brak produkcji to zwykły stan ekranu, a nie błąd wołającego.
 */
const produkcjaDwunastu = (wartosci) => {
  if (!Array.isArray(wartosci) && !ArrayBuffer.isView(wartosci)) return null
  const tablica = Array.from(wartosci)
  return tablica.length === 12 && tablica.every((v) => Number.isFinite(v) && v >= 0) && tablica.some((v) => v > 0)
    ? tablica
    : null
}

/** Miejsce do tabeli PVGIS, gdy profil go nie zna: jak w ofercie (`regionPrognozy`). */
const MIEJSCE_DOMYSLNE = jestPolska ? 'mazowieckie' : 'ro_bucuresti'

/**
 * Magazyn do modelu. Model przepustowości (`samowystarczalnoscZOkres`) liczy pełne cykle, a pełny cykl
 * to energia UŻYTKOWA, więc ją bierzemy, gdy karta ją podaje (gdy obie są podane, mniejszą z dwóch).
 * Do podpowiedzi ceny idzie pojemność nominalna, bo tak wycenia rynek.
 *
 * Sprawność z karty w procentach; wartość spoza 50–100% to raczej błąd danych niż bateria, więc wtedy
 * zostaje 90% z kalkulatora mocy.
 */
export const parametryMagazynu = (magazyn) => {
  if (!magazyn) return null
  const nominalnaKwh = dodatnia(magazyn.pojemnoscKwh)
  const uzytkowaKwh = dodatnia(magazyn.uzytkowaKwh)
  const pojemnoscKwh = nominalnaKwh && uzytkowaKwh
    ? Math.min(nominalnaKwh, uzytkowaKwh)
    : (uzytkowaKwh || nominalnaKwh)
  if (!pojemnoscKwh) return null
  const proc = dodatnia(magazyn.sprawnoscProc)
  return {
    pojemnoscKwh,
    nominalnaKwh: nominalnaKwh || pojemnoscKwh,
    uzytkowaKwh: uzytkowaKwh || null,
    sprawnosc: proc >= 50 && proc <= 100 ? proc / 100 : null,
  }
}

/**
 * Magazyn zestawu z karty katalogowej modelu (`magazynZKatalogu` w `lib/projekt3d/katalog.js`: energia
 * użytkowa i sprawność na jednostkę). Zestaw ma pojemność nominalną całości (kilka modułów), więc energię
 * użytkową przenosimy proporcją z karty. Bez karty zostaje sama pojemność nominalna.
 */
export const magazynZestawu = (pojemnoscKwh, karta = null) => {
  const nominalna = dodatnia(pojemnoscKwh)
  if (!nominalna) return null
  const naJednostke = dodatnia(karta?.pojemnoscKwh)
  const uzytkowaJednostki = dodatnia(karta?.uzytkowaKwh)
  const udzial = naJednostke && uzytkowaJednostki ? Math.min(1, uzytkowaJednostki / naJednostke) : null
  return {
    pojemnoscKwh: nominalna,
    ...(udzial ? { uzytkowaKwh: Math.round(nominalna * udzial * 100) / 100 } : {}),
    ...(dodatnia(karta?.sprawnoscProc) ? { sprawnoscProc: dodatnia(karta.sprawnoscProc) } : {}),
  }
}

/** Typowy koszt „pod klucz” (`podpowiedzKosztu`) dla mocy i pojemności NOMINALNEJ magazynu. */
export const podpowiedzKosztuProjektu = (kwp, magazyn = null) =>
  podpowiedzKosztu(dodatnia(kwp), parametryMagazynu(magazyn)?.nominalnaKwh || 0)

/**
 * Koszt instalacji i skąd go wzięliśmy. Kolejność: cena klienta z karty (brutto), kwota wpisana
 * ręcznie (np. pole kosztu w Projekcie 3D, cena oferty instalatora), suma zestawu, podane widełki,
 * typowe widełki. `null`, gdy nie ma nic (bez kosztu nie ma zwrotu).
 */
const rozstrzygnijKoszt = ({ cena, zestaw, podpowiedz, domyslnyKoszt }) => {
  const c = cena || {}
  const jedna = (kwota, zrodlo) => ({ odZl: kwota, doZl: kwota, zrodlo })
  if (dodatnia(c.bruttoZl)) return jedna(dodatnia(c.bruttoZl), c.zrodlo || 'cena_klienta')
  if (dodatnia(c.kwotaZl)) return jedna(dodatnia(c.kwotaZl), c.zrodlo || 'wpisana')
  if (dodatnia(zestaw?.kosztZl)) return jedna(dodatnia(zestaw.kosztZl), 'zestaw')
  const od = dodatnia(c.odZl)
  const doo = dodatnia(c.doZl)
  if (od || doo) {
    return { odZl: Math.min(od || doo, doo || od), doZl: Math.max(od || doo, doo || od), zrodlo: c.zrodlo || 'widelki' }
  }
  if (domyslnyKoszt && podpowiedz.od) return { odZl: podpowiedz.od, doZl: podpowiedz.do, zrodlo: 'typowe_widelki' }
  return null
}

/**
 * Produkcja pierwszego roku i jej źródło. Projekt 3D na żywo (`projekt3d.produkcjaMiesiecznaKwh`) jest
 * jedynym źródłem, gdy go podano: dopóki dach się liczy, produkcji po prostu nie ma (bez podmiany na
 * tabelę, która za chwilę zmieniłaby się w inną liczbę). Bez niego: produkcja z 3D zapisana w profilu
 * (przeskalowana do mocy zestawu, gdy moce się różnią), a na końcu tabela województwa.
 */
const rozstrzygnijProdukcje = ({ e, projekt3d, kwp }) => {
  if (projekt3d) {
    const p = produkcjaDwunastu(projekt3d.produkcjaMiesiecznaKwh)
    return p ? { miesiace: p, zrodlo: '3d' } : null
  }
  const zapisana = e.produkcja?.zrodlo === '3d' ? produkcjaDwunastu(e.produkcja.miesiaceKwh) : null
  if (zapisana) {
    const kwpZapisu = e.produkcja.kwp
    if (kwp && kwpZapisu && Math.abs(kwp / kwpZapisu - 1) > 0.005) {
      return { miesiace: zapisana.map((v) => (v * kwp) / kwpZapisu), zrodlo: '3d_przeskalowana' }
    }
    return { miesiace: zapisana, zrodlo: '3d' }
  }
  return kwp ? { miesiace: null, zrodlo: 'tabela' } : null
}

/* ═══ Silnik ═════════════════════════════════════════════════════════════════════════════════ */

const PUSTE_PODSUMOWANIE = {
  produkcjaKwh: null,
  autokonsumpcjaProc: null,
  samowystarczalnoscProc: null,
  zwrotLata: null,
  oszczednosciRoczneZl: null,
}

/**
 * Produkcja, autokonsumpcja, oszczędność i zwrot dla klienta.
 *
 * @param {object} p
 * @param {object} [p.energia] profil `dane.energia` (dowolny; normalizowany tutaj, `normalizujEnergie`)
 * @param {{kwp?:number, kosztZl?:number, kosztFalownikaZl?:number}|null} [p.zestaw] moc zestawu, suma
 *   zestawu brutto (z montażem, jak na karcie zestawu) i cena falownika z pozycji zestawu
 * @param {{bruttoZl?:number, kwotaZl?:number, odZl?:number, doZl?:number, zrodlo?:string}|null} [p.cena]
 *   `bruttoZl` cena klienta z karty (`dane.cena`), `kwotaZl` kwota wpisana ręcznie albo cena oferty,
 *   `odZl`/`doZl` widełki podane z zewnątrz; `zrodlo` nadpisuje opis źródła w `zrodlaDanych.koszt`
 * @param {{pojemnoscKwh:number, uzytkowaKwh?:number, sprawnoscProc?:number}|null} [p.magazyn] magazyn
 *   (pojemność nominalna całości, energia użytkowa i sprawność cyklu z karty, gdy znane)
 * @param {{produkcjaMiesiecznaKwh:number[]|null, kwp?:number}|null} [p.projekt3d] wynik Projektu 3D
 *   na żywo; gdy podany, produkcja pochodzi WYŁĄCZNIE z niego
 * @param {boolean} [p.domyslnyKoszt=true] bez żadnej kwoty licz z typowych widełek
 * @returns {{
 *   energia: object, zuzycieRoczne: number, wojewodztwo: string|null,
 *   produkcja: {miesiaceKwh:number[], rocznaKwh:number, uzyskKwhNaKwp:number|null, kwp:number|null, zrodlo:string}|null,
 *   wejscie: object|null, magazyn: object|null, prognoza: object|null, prognozaBezMagazynu: object|null,
 *   wartoscPierwszegoRoku: object|null, koszt: {odZl:number, doZl:number}|null,
 *   podpowiedzKosztu: {od:number|null, do:number|null}, kosztZPodpowiedzi: boolean,
 *   przeplywy: object|null, zakres: {tani, drogi, taniBezWzrostu, drogiBezWzrostu}|null,
 *   porownanieZMagazynem: object|null, rozliczenie: object,
 *   podsumowanie: {produkcjaKwh, autokonsumpcjaProc, samowystarczalnoscProc, zwrotLata, oszczednosciRoczneZl},
 *   brakuje: Array<'produkcja'|'zuzycie'|'koszt'>,
 *   zrodlaDanych: {produkcja, koszt, zuzycie, magazyn, sprawnoscMagazynu, wymianaFalownika, ceny, taryfa, lokalizacja},
 * }}
 *   `prognoza` to pierwszy rok, `przeplywy` 25 lat dla środka widełek kosztu (`lata` to seria przepływów),
 *   `zakres` zwrot na obu końcach widełek z domyślnym wzrostem cen i bez wzrostu, `porownanieZMagazynem`
 *   ten sam dom z magazynem i bez dla środka widełek (bez magazynu w zestawie: z podpowiedzianym),
 *   `rozliczenie` ceny klienta gotowe do rachunku (`rozliczenieKlienta`), `wartoscPierwszegoRoku` także
 *   miesiącami (`miesiace[i].zlDziennie`).
 */
export const ekonomiaKlienta = ({
  energia = null,
  zestaw = null,
  cena = null,
  magazyn = null,
  projekt3d = null,
  domyslnyKoszt = true,
} = {}) => {
  const e = normalizujEnergie(energia)
  const zuzycie = rocznyKwh(e)
  const mag = parametryMagazynu(magazyn)
  const kwp = dodatnia(zestaw?.kwp) || dodatnia(projekt3d?.kwp) || dodatnia(e.produkcja?.kwp) || 0
  const podpowiedz = podpowiedzKosztuProjektu(kwp, magazyn)
  const koszt = rozstrzygnijKoszt({ cena, zestaw, podpowiedz, domyslnyKoszt })
  const zrodloProdukcji = rozstrzygnijProdukcje({ e, projekt3d, kwp })
  const kosztFalownikaZl = dodatnia(zestaw?.kosztFalownikaZl) || null
  const wojLokalizacji = e.lokalizacja.wojewodztwo
    || wojewodztwoZeWspolrzednych(e.lokalizacja.lat, e.lokalizacja.lon)
  const rozliczenie = rozliczenieKlienta({
    taryfa: e.taryfa,
    cenaZakupu: e.cenaZakupu,
    cenaZakupuTania: e.cenaZakupuTania,
    cenaSprzedazy: e.cenaSprzedazy,
    systemRozliczen: e.systemRozliczen,
  })

  const wynik = {
    energia: e,
    zuzycieRoczne: zuzycie,
    wojewodztwo: wojLokalizacji,
    produkcja: null,
    wejscie: null,
    magazyn: mag,
    prognoza: null,
    prognozaBezMagazynu: null,
    wartoscPierwszegoRoku: null,
    koszt: koszt ? { odZl: koszt.odZl, doZl: koszt.doZl } : null,
    podpowiedzKosztu: podpowiedz,
    kosztZPodpowiedzi: koszt?.zrodlo === 'typowe_widelki',
    przeplywy: null,
    zakres: null,
    porownanieZMagazynem: null,
    rozliczenie,
    podsumowanie: { ...PUSTE_PODSUMOWANIE },
    brakuje: [!zrodloProdukcji && 'produkcja', !zuzycie && 'zuzycie', !koszt && 'koszt'].filter(Boolean),
    zrodlaDanych: {
      produkcja: zrodloProdukcji?.zrodlo || null,
      koszt: koszt?.zrodlo || null,
      zuzycie: zuzycie ? e.zuzycie.sposob : null,
      magazyn: mag ? (mag.uzytkowaKwh ? 'uzytkowa_z_karty' : 'nominalna') : null,
      sprawnoscMagazynu: mag ? (mag.sprawnosc != null ? 'karta' : 'zalozenie') : null,
      wymianaFalownika: kosztFalownikaZl ? 'zestaw' : 'udzial',
      ceny: e.cenaZakupu != null || e.cenaZakupuTania != null || e.cenaSprzedazy != null ? 'klient' : 'zalozenia',
      taryfa: e.taryfa === 'nie_wiem' ? 'nie_wiem' : (e.taryfa ? 'klient' : 'domyslna'),
      lokalizacja: e.lokalizacja.wojewodztwo ? 'nazwa' : (wojLokalizacji ? 'wspolrzedne' : 'domyslna'),
    },
  }
  if (!zrodloProdukcji) return wynik

  // Kształt `wejscie` jak dotąd w ofercie (tabela) i w Projekcie 3D (produkcja dachu), żeby wynik przy tych
  // samych danych był co do bitu ten sam co przed silnikiem.
  const wejscie = {
    kwp: kwp || undefined,
    wojewodztwo: wojLokalizacji || MIEJSCE_DOMYSLNE,
    ustawienie: e.ustawienie,
    zacienienie: e.zacienienie,
    ...(zrodloProdukcji.miesiace ? { produkcjaMiesiecznaKwh: zrodloProdukcji.miesiace } : {}),
    zuzycieRoczne: zuzycie,
    profil: e.profil,
    dodatki: e.dodatki,
    pojemnoscMagazynuKwh: mag ? mag.pojemnoscKwh : 0,
    ...(mag?.sprawnosc != null ? { sprawnoscMagazynu: mag.sprawnosc } : {}),
  }
  wynik.wejscie = wejscie
  wynik.prognoza = prognoza(wejscie)
  if (mag) wynik.prognozaBezMagazynu = prognoza({ ...wejscie, pojemnoscMagazynuKwh: 0 })
  const r = wynik.prognoza.rocznie
  wynik.wartoscPierwszegoRoku = wartoscEnergii(wynik.prognoza, rozliczenie)
  wynik.produkcja = {
    miesiaceKwh: wynik.prognoza.miesiace.map((m) => m.produkcjaKwh),
    rocznaKwh: r.produkcjaKwh,
    uzyskKwhNaKwp: wynik.prognoza.uzyskJednostkowy,
    kwp: kwp || null,
    zrodlo: zrodloProdukcji.zrodlo,
  }

  const wzrost = e.wzrostCen ?? WZROST_CEN_DOMYSLNY
  if (zuzycie && koszt) {
    const wspolne = { wejscie, kosztFalownikaZl, ulga: kluczUlgi(e.ulga), dotacjaZl: e.dotacjaZl || 0, rozliczenie }
    const jedenKoszt = koszt.odZl === koszt.doZl
    const srodek = Math.round((koszt.odZl + koszt.doZl) / 2)
    wynik.przeplywy = przeplywy({ koszt: srodek, wzrostCen: wzrost, ...wspolne })
    const tani = jedenKoszt ? wynik.przeplywy : przeplywy({ koszt: koszt.odZl, wzrostCen: wzrost, ...wspolne })
    const taniBezWzrostu = przeplywy({ koszt: koszt.odZl, wzrostCen: 0, ...wspolne })
    wynik.zakres = {
      tani,
      drogi: jedenKoszt ? tani : przeplywy({ koszt: koszt.doZl, wzrostCen: wzrost, ...wspolne }),
      taniBezWzrostu,
      drogiBezWzrostu: jedenKoszt ? taniBezWzrostu : przeplywy({ koszt: koszt.doZl, wzrostCen: 0, ...wspolne }),
    }
    wynik.porownanieZMagazynem = porownajZMagazynem({ koszt: srodek, wzrostCen: wzrost, ...wspolne })
  }

  wynik.podsumowanie = {
    produkcjaKwh: Math.round(r.produkcjaKwh),
    autokonsumpcjaProc: r.autokonsumpcjaProc,
    samowystarczalnoscProc: r.samowystarczalnoscProc,
    zwrotLata: wynik.przeplywy ? wynik.przeplywy.zwrotLata : null,
    oszczednosciRoczneZl: wynik.wartoscPierwszegoRoku ? Math.round(wynik.wartoscPierwszegoRoku.razemZl) : null,
  }
  return wynik
}

/* ═══ Wejście z VoltValue Projektu (oferta) ═════════════════════════════════════════════════ */

/**
 * Profil z odpowiedzi VoltValue Projektu (`lib/daneProjektu.js`, dziś z sessionStorage karty przeglądarki).
 * Tam `zuzycieRoczne` to już CAŁE zużycie (kalkulator mocy dolicza odbiorniki, zanim je zapisze), a odbiorniki
 * tylko rozkładają je na miesiące, stąd `dodatkiWliczone`. Ulga zapisana kluczem progu (`p12`).
 */
export const energiaZDanychProjektu = (d = {}) => ({
  zuzycie: { sposob: 'kwh', kwh: Number(d.zuzycieRoczne) || null, dodatkiWliczone: true, zrodlo: 'oferta' },
  profil: d.profil,
  dodatki: d.dodatki,
  wzrostCen: d.wzrostCen,
  ulga: d.ulga,
  dotacjaZl: d.dotacjaZl,
  lokalizacja: { wojewodztwo: d.wojewodztwo },
  ustawienie: d.ustawienie,
  zacienienie: d.zacienienie,
})

/**
 * Wejście silnika dla oferty (okno VoltValue Projektu i `/sprawdz-oferte`).
 *
 * @param {object} p
 * @param {object} p.daneProjektu odpowiedzi z `odczytajDaneProjektu()` (zużycie, województwo, ulga…)
 * @param {{kwp:number, kosztFalownikaZl?:number, pojemnoscMagazynuKwh?:number}} p.zestaw `podsumujZestaw`
 * @param {number|null} [p.cenaKlientaZl] cena brutto z karty klienta (oferta z karty)
 * @param {number|null} [p.kosztZestawuZl] suma zestawu brutto z rachunku (gdy oferta ma koszty)
 * @param {number|null} [p.cenaOfertyZl] cena spoza rachunku, np. oferta instalatora w `/sprawdz-oferte`
 * @param {object|null} [p.kartaMagazynu] `magazynZKatalogu` modelu magazynu (energia użytkowa, sprawność)
 * @param {object|null} [p.energia] profil klienta; gdy podany, zastępuje odpowiedzi VoltValue Projektu
 *   (etap 1: oferta przy kliencie czyta `dane.energia`)
 */
export const wejscieOferty = ({
  daneProjektu = {}, zestaw, cenaKlientaZl = null, kosztZestawuZl = null, cenaOfertyZl = null,
  kartaMagazynu = null, energia = null,
}) => ({
  energia: energia || energiaZDanychProjektu(daneProjektu),
  zestaw: {
    kwp: zestaw?.kwp,
    kosztZl: kosztZestawuZl,
    kosztFalownikaZl: zestaw?.kosztFalownikaZl,
  },
  cena: {
    bruttoZl: cenaKlientaZl,
    kwotaZl: cenaOfertyZl,
    zrodlo: cenaKlientaZl ? 'cena_klienta' : (cenaOfertyZl ? 'cena_oferty' : undefined),
    odZl: daneProjektu.kosztOd,
    doZl: daneProjektu.kosztDo,
  },
  magazyn: magazynZestawu(zestaw?.pojemnoscMagazynuKwh, kartaMagazynu),
})

